import { useCallback, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import {
  AnswerOption,
  AnswerOptions,
} from '@/features/jobs/questions/components/AnswerOptions'
import { type SelectQuestionAnswerEditorViewModel } from '@/features/jobs/questions/select/SelectQuestionAnswerEditorViewModel'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import {
  useViewModelBackedAriaMultiSelection,
  useViewModelBackedAriaSingleSelection,
} from '@/hooks/aria-selection-hooks'
import { MultiSelect } from '@/components/inputs/MultiSelect'
import type { SelectQuestionOption } from '@/features/jobs/questions/questions'

interface SelectQuestionAnswerEditorContainerProps {
  questionId: string
  editorVm: SelectQuestionAnswerEditorViewModel
  isDisabled?: boolean
  isInvalid?: boolean
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

export const SelectQuestionAnswerEditorContainer = observer(
  function SelectQuestionAnswerEditorContainer({
    editorVm,
    isDisabled,
    ...rest
  }: SelectQuestionAnswerEditorContainerProps) {
    const options = editorVm.options

    const disabledKeys = useMemo(
      () => (isDisabled ? options.map((item) => item.id) : []),
      [isDisabled, options],
    )

    return editorVm.multi ? (
      options.length >= 10 ? (
        <MultiSelectManyItemsContainer
          editorVm={editorVm}
          isDisabled={isDisabled}
          {...rest}
        />
      ) : (
        <MultiSelectContainer
          editorVm={editorVm}
          disabledKeys={disabledKeys}
          {...rest}
        />
      )
    ) : (
      <SingleSelectContainer
        editorVm={editorVm}
        disabledKeys={disabledKeys}
        {...rest}
      />
    )
  },
)

interface SingleSelectContainerProps extends SelectQuestionAnswerEditorContainerProps {
  disabledKeys: string[]
}

/**
 * Single Select Container
 */
export const SingleSelectContainer = observer(function SingleSelectContainer({
  editorVm,
  disabledKeys,
  questionId,
  ...rest
}: SingleSelectContainerProps) {
  const isSpacious = useIsSpacious()
  const { value, multi, options } = editorVm

  const selectedItem = useMemo(() => {
    return options.find((o) => value.includes(o.id)) ?? null
  }, [options, value])

  const selectionProps = useViewModelBackedAriaSingleSelection({
    disallowEmptySelection: true,
    selectedItem,
    items: options,
    getKey: (item) => item.id,
    onSelectionChange: (item) => {
      if (!multi) {
        editorVm.respond([item.id])
      }
    },
  })

  return (
    <AnswerOptions
      mode="single"
      layout={isSpacious ? 'grid' : 'stack'}
      items={options}
      disabledKeys={disabledKeys}
      testId={`select-question-answer-editor-${questionId}`}
      {...selectionProps}
      {...rest}
    >
      {(item) => (
        <AnswerOption
          testId={`select-question-answer-editor-option-${questionId}-${item.id}`}
          id={item.id}
          label={item.text}
        />
      )}
    </AnswerOptions>
  )
})

interface MultiSelectContainerProps extends SelectQuestionAnswerEditorContainerProps {
  disabledKeys: string[]
}

/**
 * Multi Select Container
 */
export const MultiSelectContainer = observer(function MultiSelectContainer({
  editorVm,
  disabledKeys,
  questionId,
  ...rest
}: MultiSelectContainerProps) {
  const isSpacious = useIsSpacious()
  const { value, options } = editorVm

  const selectedItems = useMemo(() => {
    return options.filter((o) => value.includes(o.id))
  }, [options, value])

  const selectionProps = useViewModelBackedAriaMultiSelection({
    disallowEmptySelection: false,
    selectedItems,
    items: options,
    getKey: (item) => item.id,
    onSelectionChange: (selection) => {
      editorVm.respond(selection.map((o) => o.id))
    },
  })

  return (
    <AnswerOptions
      mode="multi"
      layout={isSpacious ? 'grid' : 'stack'}
      items={options}
      disabledKeys={disabledKeys}
      testId={`select-question-answer-editor-${questionId}`}
      {...selectionProps}
      {...rest}
    >
      {(item) => (
        <AnswerOption
          testId={`select-question-answer-editor-option-${questionId}-${item.id}`}
          id={item.id}
          label={item.text}
        />
      )}
    </AnswerOptions>
  )
})

interface MultiSelectManyItemsContainerProps extends SelectQuestionAnswerEditorContainerProps {}

/**
 * Multi Select Container for select questions with many options.
 */
export const MultiSelectManyItemsContainer = observer(
  function MultiSelectManyItemsContainer({
    editorVm,
    isDisabled,
    questionId,
    ...rest
  }: MultiSelectManyItemsContainerProps) {
    const { value, options } = editorVm

    const selectedKeys = useMemo(() => {
      return new Set(
        options.flatMap((o) => (value.includes(o.id) ? [o.id] : [])),
      )
    }, [options, value])

    const getKey = useCallback((option: SelectQuestionOption) => option.id, [])
    const getLabel = useCallback(
      (option: SelectQuestionOption) => option.text,
      [],
    )
    const onSelectionChange = useCallback(
      (keys: Set<string>) => editorVm.respond(Array.from(keys)),
      [editorVm],
    )

    return (
      <MultiSelect
        isDisabled={isDisabled}
        items={options}
        selectedKeys={selectedKeys}
        placeholder="Select all that apply"
        onSelectionChange={onSelectionChange}
        getKey={getKey}
        getLabel={getLabel}
        testId={`select-question-answer-editor-${questionId}`}
        {...rest}
      />
    )
  },
)
